import { ReactNode } from 'react';
import Link from 'next/link';
import { Ir, NamVsIr, Nam } from '../Tables/NamVsIr';
import A2Mushra from '@/components/ui/Charts/A2Mushra';
import A2Elo from '@/components/ui/Charts/A2Elo';
import {
  A2_ANNOUNCEMENT_URL,
  A2_GUIDE_URL,
  A2_LISTENING_TEST_URL,
  A2_UNSUPPORTED_HARDWARE_URL,
  DRY_WET_CAPTURE_GUIDE_URL,
  GITHUB_ORG_URL,
  IR_ACOUSTIC_SPACE_CAPTURE_GUIDE_URL,
  IR_EFFECTS_UNIT_CAPTURE_GUIDE_URL,
  IR_GUIDE_URL,
  IR_SPEAKER_CABINET_CAPTURE_GUIDE_URL,
  MUSHRA_TEST_DATA_URL,
  NAM_GUIDE_URL,
  PLUGIN_GUIDE_URL,
  SWEEP_CAPTURE_GUIDE_URL,
} from '@/utils/constants';
import { PLUGIN_GITHUB_URL } from '@/utils/pluginDownloads';

const getPlainTextFromStringOrElement = (text: string | ReactNode) => {
  const extract = (node: ReactNode): string => {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map((child) => extract(child)).join(' ');
    if (typeof node === 'object') {
      const element: any = node as any;
      if (element && element.props && 'children' in element.props) {
        return extract(element.props.children);
      }
      return '';
    }
    return '';
  };

  const raw = typeof text === 'string' ? text : extract(text);
  return raw.replace(/\s+/g, ' ').trim();
};

export const generalFaq: { title: string; description: string | JSX.Element }[] = [
  {
    title: 'What is TONE3000?',
    description:
      'TONE3000 (formerly ToneHunt) is a free community platform where users share hyper-accurate digital models of their analog gear. Access iconic amp models, including the Fender Twin Reverb, Marshall JCM 800, and Vox AC30, plus hundreds of thousands more!',
  },
  {
    title: 'How does TONE3000 work?',
    description:
      'Users capture their own gear using AI technology and share the models on tone3000.com. You can download these captures to use in your DAW for jamming or recording. You can even load them into compatible pedals for live performances.',
  },
  {
    title: 'What are Neural Amp Modeler (NAM) and Impulse Response (IR)?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          Neural Amp Modeler (NAM): Free, open-source AI technology that captures the exact sound
          and feel of real amplifiers. Unlike traditional amp sims, NAM replicates every detail of
          the original gear – from vintage tube warmth to modern high-gain crunch.
        </p>
        <p>
          Impulse Response (IR): Technology for capturing speaker cabinets, reverb, and room
          acoustics.
        </p>

        <NamVsIr />
      </div>
    ),
  },
  {
    title: 'What are the benefits of digital models of music gear?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          Digital models cut the cost and hassle of physical gear while giving you endless options:
        </p>
        <ul className={'list-disc pl-4'}>
          <li>Jam at home with 100K+ tones using headphones or monitors</li>
          <li>Record studio-quality tracks without studio fees</li>
          <li>Keep your favorite tones and gear neatly organized</li>
          <li>Experiment without retracking, reamping, or setup</li>
          <li>Recreate tones even without your original signal chain or settings</li>
          <li>Share tones with friends, family, or partners</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'How do I start using tones from TONE3000?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <ol className={'list-decimal pl-4 space-y-2'}>
          <li>
            Download the official{' '}
            <Link href="/plugin" className="text-white underline">
              TONE3000 Plugin
            </Link>
            . It's free, and works in your DAW or as a standalone app.
          </li>
          <li>Connect your instrument to your audio interface.</li>
          <li>Browse a tone in the plugin and start playing.</li>
        </ol>
      </div>
    ),
  },
];

const namFaq: { title: string; description: JSX.Element } = {
  title: 'What is Neural Amp Modeler (NAM)?',
  description: (
    <div className={'flex flex-col gap-4'}>
      <p>
        Neural Amp Modeler (NAM): Free, open-source AI technology that captures the exact sound and
        feel of real amplifiers. Unlike traditional amp sims, NAM replicates every detail of the
        original gear – from vintage tube warmth to modern high-gain crunch.
      </p>

      <div className={'flex flex-col lg:flex-row gap-4 w-full'}>
        {/* NAM Captures Table */}
        <Nam />
      </div>
    </div>
  ),
};

const irFaq: { title: string; description: JSX.Element } = {
  title: 'What is an Impulse Response (IR)?',
  description: (
    <div className={'flex flex-col gap-4'}>
      <p>
        Impulse Response (IR): Technology for capturing speaker cabinets, reverb, and room
        acoustics.
      </p>

      <div className={'flex flex-col lg:flex-row gap-4 w-full'}>
        {/* IR Captures Table */}
        <Ir />
      </div>
    </div>
  ),
};

export type FaqType = 'general' | 'nam' | 'ir' | 'plugin';

export const pluginFaq: { title: string; description: string | JSX.Element }[] = [
  {
    title: 'What is the TONE3000 Plugin?',
    description: (
      <p>
        The TONE3000 Plugin is our official, free{' '}
        <Link className="underline" href={NAM_GUIDE_URL}>
          Neural Amp Modeler
        </Link>{' '}
        (NAM) plugin for guitar and bass. Browse and play TONE3000&apos;s massive library of NAM
        captures and impulse responses directly inside the plugin, either in your DAW or as a
        standalone app.
      </p>
    ),
  },
  {
    title: 'Is the TONE3000 Plugin free?',
    description: <p>Yes. The TONE3000 Plugin is completely free to download and use.</p>,
  },
  {
    title: 'Is TONE3000 a free guitar and bass amp sim?',
    description: (
      <p>
        Yes. TONE3000 is a free guitar and bass amp sim powered by{' '}
        <Link className="underline" href={A2_GUIDE_URL}>
          NAM A2
        </Link>
        . Instead of a small, fixed collection of amps, you can browse NAM captures of amps, pedals,
        cabinets, full rigs, outboard gear, and more from the TONE3000 community.
      </p>
    ),
  },
  {
    title: 'Is TONE3000 a NAM plugin or NAM player?',
    description: (
      <p>
        Yes. The TONE3000 Plugin is a NAM player built for browsing and playing Neural Amp Modeler
        captures. Instead of downloading NAM files, organizing them on your computer, and loading
        them into another plugin, you can find a tone on TONE3000 and play it directly inside the
        plugin or standalone app.
      </p>
    ),
  },
  {
    title: 'Do I need a DAW to use the TONE3000 Plugin?',
    description: (
      <p>
        No. TONE3000 works as a standalone app, so you can connect your guitar or bass, choose a
        tone, and start playing without a DAW. You can also use the plugin inside your DAW for
        recording and production.
      </p>
    ),
  },
  {
    title: 'Which plugin formats does the TONE3000 Plugin support?',
    description: (
      <p>
        The TONE3000 Plugin is available as VST3, AU, AAX, LV2, and CLAP, plus a standalone app that
        works without a DAW. It supports macOS, Windows, and Linux.
      </p>
    ),
  },
  {
    title: 'What is Neural Amp Modeler (NAM)?',
    description: (
      <p>
        <Link className="underline" href={NAM_GUIDE_URL}>
          Neural Amp Modeler
        </Link>{' '}
        (NAM) is open-source technology for creating hyper-accurate digital models, called NAM
        captures or NAM profiles, of real analog gear such as guitar and bass amps, pedals, outboard
        gear, and full signal chains.
      </p>
    ),
  },
  {
    title: 'What is NAM A2?',
    description: (
      <p>
        <Link className="underline" href={A2_GUIDE_URL}>
          NAM A2
        </Link>{' '}
        is the latest generation of Neural Amp Modeler, created by TONE3000 in partnership with
        Steve Atkinson. In quantitative tests and a{' '}
        <Link className="underline" href={A2_LISTENING_TEST_URL}>
          blind listening test
        </Link>{' '}
        with more than 1,000 participants, NAM A2 beat Neural DSP Neural Capture V2, IK Multimedia
        ToneX V2, and Line 6 Proxy by a wide margin, making it the best-sounding amp modeling
        technology on the market.
      </p>
    ),
  },
  {
    title: 'Can I use TONE3000 for bass?',
    description: (
      <p>
        Yes. TONE3000 is built for both bass and guitar. You can browse and play captures of bass
        amps, bass pedals, speaker cabinets, bass rigs, and more inside the plugin.
      </p>
    ),
  },
  {
    title: 'How is the TONE3000 Plugin different from Neural DSP plugins, AmpliTube, or Line 6 Helix Native?',
    description: (
      <p>
        The TONE3000 Plugin and its entire library of tones are completely free, unlike Neural DSP
        plugins, AmpliTube, and Line 6 Helix Native, which are paid products. TONE3000 is also
        powered by NAM A2, an open-source modeling technology that significantly outperformed Neural
        DSP Neural Capture V2, IK Multimedia ToneX V2, and Line 6 Proxy on sound quality and
        accuracy in quantitative testing and a blind listening test with more than 1,000
        participants.
      </p>
    ),
  },
  {
    title: 'Is the TONE3000 Plugin open source?',
    description: (
      <p>
        Yes. The TONE3000 Plugin is open source. Developers can inspect the source code, report
        issues, request features, and contribute improvements through{' '}
        <Link
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
          href={PLUGIN_GITHUB_URL}
        >
          GitHub
        </Link>
        .
      </p>
    ),
  },
];

export const getFaq = (
  type: FaqType
): { title: string; description: string | JSX.Element }[] => {
  let faqs = generalFaq;
  if (type === 'plugin') {
    return pluginFaq;
  }
  if (type === 'general') {
    return faqs;
  }
  if (type === 'nam') {
    faqs[2] = namFaq;
    return faqs;
  }
  if (type === 'ir') {
    faqs[2] = irFaq;
    return faqs;
  }
  return faqs;
};

export const getPlainTextFaq = (
  type: FaqType
): { title: string; description: string }[] => {
  const faqs = getFaq(type);
  return faqs.map((faq) => ({
    title: faq.title,
    description: getPlainTextFromStringOrElement(faq.description),
  }));
};

export const generalSteps = [
  {
    title: 'Is TONE3000 free?',
    description: (
      <p>
        Yes.{' '}
        <Link className={'underline'} target={'_blank'} href={'/search'}>
          Browsing
        </Link>
        , downloading,{' '}
        <Link className={'underline'} target={'_blank'} href={'/capture'}>
          capturing your gear
        </Link>
        , and sharing tones are all free. It's all free!
      </p>
    ),
  },
  {
    title: 'How is TONE3000 useful for me?',
    description:
      'TONE3000 gives you access to hundreds of thousands of digital models of amps, pedals, full rigs, outboard gear, and signal chains. Use them to make studio-quality recordings at home, or load them onto compatible hardware to perform live.',
  },
  {
    title: 'What are the benefits of digital models of audio equipment?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          Digital models save you time and money while providing endless choices. Popular uses
          include:
        </p>
        <ul className={'list-disc pl-4'}>
          <li>Create studio-quality recordings without expensive studio sessions</li>
          <li>Play live without lugging around expensive and heavy gear that's prone to damage</li>
          <li>An organized library of your favorite tones</li>
          <li>Experiment with tones without retracking, reamping or setting up gear</li>
          <li>Recreate tones (e.g. tonematch) even if you forgot your signal chain or settings</li>
          <li>Share your favorite tones with friends, family or lovers</li>
          <li>
            Play models of your gear through headphones/monitors instead of blasting through a wall
            of 4x12s and having your neighbors call the police
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: 'How is TONE3000 different from amp modeling pedals like Neural DSP, ToneX and Line6?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          Amp modeling pedals cost $500 to $2,000+ and require you to use their hardware and
          software. With T3K, you can capture tones for free on your computer, and those tones work
          anywhere NAM or IR files are supported, just like WAV or MIDI files.
        </p>
        <p>
          Sound quality is another difference.{' '}
          <Link className={'underline'} target={'_blank'} href={A2_GUIDE_URL}>
            NAM A2
          </Link>{' '}
          beats Neural DSP V2, IK Multimedia ToneX V2, and Line 6 Proxy by a wide margin in both
          quantitative and{' '}
          <Link className={'underline'} target={'_blank'} href={A2_LISTENING_TEST_URL}>
            blind listening tests
          </Link>
          . Both the{' '}
          <Link className={'underline'} target={'_blank'} href={MUSHRA_TEST_DATA_URL}>
            test code and raw data
          </Link>{' '}
          are open source.
        </p>

        <A2Mushra />
        <A2Elo />
      </div>
    ),
  },
  {
    title: 'What is Neural Amp Modeler?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          <Link className={'underline'} href={NAM_GUIDE_URL} target={'_blank'}>
            Neural Amp Modeler
          </Link>{' '}
          is an open-source project created by Steve Atkinson that uses deep learning to create
          digital models of guitar amplifiers, pedals and signal chains with state-of-the-art
          accuracy.
        </p>
      </div>
    ),
  },
  {
    title: 'What is NAM Architecture 2 (A2)?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          <Link className={'underline'} target={'_blank'} href={A2_ANNOUNCEMENT_URL}>
            NAM A2
          </Link>{' '}
          is the current generation of Neural Amp Modeler, built by TONE3000 in partnership with
          Steve Atkinson. It's more accurate than any modeler that came before it, and efficient
          enough to run on a $3 chip. Full details are in{' '}
          <Link className={'underline'} target={'_blank'} href={A2_GUIDE_URL}>
            NAM A2: The Complete Guide
          </Link>
          .
        </p>
      </div>
    ),
  },
  {
    title: 'Is A2 open source?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          Yes, fully MIT-licensed. The architecture, training code, and inference engines are free
          to use, modify, and ship in commercial products with no license fees. Everything is on{' '}
          <Link className={'underline'} target={'_blank'} href={GITHUB_ORG_URL}>
            GitHub
          </Link>
          .
        </p>
      </div>
    ),
  },
  {
    title: 'What does "neural modeling" mean?',
    description:
      'Neural modeling uses machine learning to accurately replicate the tone created by audio equipment. Instead of relying on traditional digital modeling, neural models "learn" the unique sonic characteristics of gear from recordings.',
  },
  {
    title: 'Why are there multiple models for each gear capture?',
    description:
      'Digital modeling currently captures a "snapshot" of gear at specific knob settings. To cover different settings, users create multiple models.',
  },
  {
    title: 'What is a tone pack?',
    description: `A tone pack (sometimes called "tone" for short) is a collection of models or impulse responses (IR's). A single pack holds every architecture, so A2, A1, and custom models can live together and the host app loads whichever it supports.`,
  },
  {
    title: 'What is an IR?',
    description: (
      <p>
        An Impulse Response (IR) is a digital model of a speaker cabinet, reverb, or room acoustics,
        captured as a waveform. Learn more on our guide:{' '}
        <Link className={'underline'} target={'_blank'} href={IR_GUIDE_URL}>
          Impulse Response (IR)
        </Link>
      </p>
    ),
  },
  {
    title: 'Can I hear the tones on TONE3000?',
    description: (
      <p>
        Yes, two ways. The preview player lets you audition every tone with a variety of DIs and
        IR's.{' '}
        <Link
          className={'underline'}
          target={'_blank'}
          href={'/blog/introducing-live-input-on-tone3000'}
        >
          Live Input
        </Link>{' '}
        lets you plug your instrument into your audio interface and play through any tone in the
        library in real time, right in your browser.
      </p>
    ),
  },
  {
    title: 'Can I share tones with others?',
    description: 'Yes. Simply share the link to the captured tone.',
  },
  {
    title: 'What is a Verified Profile?',
    description: (
      <p>
        Verified Profiles are official accounts for gear manufacturers, retailers, and top creators.
        Brands like{' '}
        <Link
          className={'underline'}
          target={'_blank'}
          href={'/blog/blackstar-free-neural-amp-modeler-nam-captures'}
        >
          Blackstar
        </Link>{' '}
        and{' '}
        <Link
          className={'underline'}
          target={'_blank'}
          href={'/blog/andertons-tone3000-verified-profile'}
        >
          Andertons
        </Link>{' '}
        share official captures of their gear directly with the community. You can also browse the{' '}
        <Link className={'underline'} target={'_blank'} href={'/top-creators'}>
          creator leaderboard
        </Link>
        .
      </p>
    ),
  },
  {
    title: `What's your Tone Sharing Policy?`,
    description: (
      <p>
        TONE3000 is for sharing captures of real analog gear. You can view the policy here:{' '}
        <Link
          className={'underline'}
          href={'/guides/tone-sharing-guidelines#tone-sharing-policy'}
          target={'_blank'}
        >
          Tone Sharing Policy
        </Link>
      </p>
    ),
  },
  {
    title: 'Why the name TONE3000?',
    description: 'Our favorite gear has numbers in its name… so why not ours?',
  },
];

export const gettingStartedSteps = [
  {
    title: `I'm brand new to this. How do I use TONE3000?`,
    description: (
      <p>
        Download the official{' '}
        <Link className={'underline'} href="/plugin">
          TONE3000 Plugin
        </Link>
        . It's free, works in your DAW or as a standalone app, and the{' '}
        <Link className={'underline'} href={PLUGIN_GUIDE_URL}>
          Plugin Guide
        </Link>{' '}
        walks you through it.
      </p>
    ),
  },
  {
    title: 'What hardware and software works with TONE3000?',
    description: (
      <p>
        The official{' '}
        <Link className={'underline'} href="/plugin">
          TONE3000 Plugin
        </Link>{' '}
        works on Mac, Windows, and Linux. Tones also play in compatible plugins, pedals, and amps —
        the ecosystem is growing fast, so follow the{' '}
        <Link className={'underline'} href={'/blog'}>
          blog
        </Link>{' '}
        for the latest.
      </p>
    ),
  },
  {
    title: `What if my hardware or software doesn't support TONE3000 or NAM A2?`,
    description: (
      <p>
        You can always play tones in the official{' '}
        <Link className={'underline'} href="/plugin">
          TONE3000 Plugin
        </Link>
        . For other hardware and software, ask the company directly. The more people who request it,
        the faster it gets prioritized. We've written a message you can copy, edit, and send, plus
        contact details for most major manufacturers:{' '}
        <Link className={'underline'} target={'_blank'} href={A2_UNSUPPORTED_HARDWARE_URL}>
          What if my hardware or software doesn't support A2?
        </Link>
      </p>
    ),
  },
];

export const capturingGearSteps = [
  {
    title: 'I already capture/train models on Colab or my local setup. Will T3K be useful for me?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>We hope so! Here's why:</p>
        <ul className={'list-disc pl-4'}>
          <li>Fast, free training on RTX-4090s (no need to use your own resources)</li>
          <li>A2 by default, with both A2-Full and A2-Lite in a single model</li>
          <li>Dead simple workflow</li>
          <li>Cloud-based: start training and close your computer</li>
          <li>Train multiple models concurrently, including batch training</li>
          <li>Organized library of your tones</li>
          <li>Easily share your models</li>
          <li>Preview models with a range of DIs, or play through them live in the browser</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'How does capturing (also known as "training" or "profiling") on T3K work?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <ol className={'pl-4 list-decimal'}>
          <li>
            Upload either a recording of the provided{' '}
            <Link
              className={'underline'}
              target={'_blank'}
              href={SWEEP_CAPTURE_GUIDE_URL}
            >
              sweep signal
            </Link>{' '}
            through your gear or a{' '}
            <Link
              className={'underline'}
              target={'_blank'}
              href={DRY_WET_CAPTURE_GUIDE_URL}
            >
              dry/wet signal
            </Link>{' '}
            pair (DI and matching stem).
          </li>
          <li>
            T3K trains your tone model on cloud GPUs, no complex local setups or coding required.
          </li>
          <li>
            Download your model as an open source NAM file, which works anywhere NAM is supported.
          </li>
        </ol>
        <p>
          New to this? Start with the{' '}
          <Link className={'underline'} target={'_blank'} href={SWEEP_CAPTURE_GUIDE_URL}>
            capturing guide
          </Link>
          .
        </p>
      </div>
    ),
  },
  {
    title: 'Can I capture impulse responses too?',
    description: (
      <p>
        We have guides for capturing a{' '}
        <Link className={'underline'} target={'_blank'} href={IR_SPEAKER_CABINET_CAPTURE_GUIDE_URL}>
          speaker cabinet
        </Link>
        , an{' '}
        <Link className={'underline'} target={'_blank'} href={IR_ACOUSTIC_SPACE_CAPTURE_GUIDE_URL}>
          acoustic space
        </Link>
        , and an{' '}
        <Link className={'underline'} target={'_blank'} href={IR_EFFECTS_UNIT_CAPTURE_GUIDE_URL}>
          effects unit
        </Link>
        .
      </p>
    ),
  },
  {
    title: 'How does T3K cover the cost of training models?',
    description: `We're a venture-backed studio, which allows us to offer free GPU resources for you.`,
  },
  {
    title: 'How long does model training take?',
    description: 'Training NAM models typically takes a few minutes.',
  },
  {
    title: 'Why do you offer gear capture?',
    description:
      'Local training setups are too complex and amp modeling pedals are too expensive. T3K makes it simple, fast, and free to train models, with a sleek UX that (hopefully) anyone can use.',
  },
  {
    title: 'What is your data policy?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          The following are direct quotes from our{' '}
          <Link target={'_blank'} href={'/privacy'} className={'underline'}>
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link target={'_blank'} href={'/terms'} className={'underline'}>
            Terms of Service
          </Link>
          :
        </p>

        <ul className={'list-disc pl-4'}>
          <li>
            You retain full ownership of all audio files, models, and related data you upload.
          </li>
          <li>
            TONE3000 does not claim ownership of your uploaded content and will not sell your data
            or models.
          </li>
          <li>
            By uploading files to TONE3000, you grant us a non-exclusive, royalty-free license to
            use the data for internal research purposes to develop and improve our services.
          </li>
          <li>
            This license is limited to internal purposes and does not grant us the right to share,
            sell, or distribute your data.
          </li>
        </ul>

        <p>
          Two examples of that internal research:{' '}
          <Link target={'_blank'} className={'underline'} href={'/blog/introducing-best-fit'}>
            Best Fit
          </Link>
          , which delivers the same quality model faster, and the development of{' '}
          <Link target={'_blank'} className={'underline'} href={A2_GUIDE_URL}>
            NAM A2
          </Link>
          .
        </p>

        <p>
          If you would like to opt out of having your data used for research purposes, please email
          us at{' '}
          <Link className={'underline'} href={'mailto:support@tone3000.com'}>
            support@tone3000.com
          </Link>
          .
        </p>
      </div>
    ),
  },
  {
    title: 'Can I use T3K in unintended ways?',
    description: (
      <p>
        Absolutely! We have an entire category of{' '}
        <Link className={'underline'} target={'_blank'} href={'/search?gears=experimental'}>
          experimental captures
        </Link>
        .
      </p>
    ),
  },
];

export const developersSteps = [
  {
    title: 'Does TONE3000 have an API?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          Yes. The{' '}
          <Link className={'underline'} target={'_blank'} href={'/api'}>
            TONE3000 API
          </Link>{' '}
          allows hardware and software makers to bring our huge library of NAM captures, IRs, and
          the TONE3000 account experience directly into their products.
        </p>
        <p>
          At the time of this writing,{' '}
          <Link className={'underline'} target={'_blank'} href={'/blog/jordan-valeriote-nam-rig'}>
            NAM Rig
          </Link>
          , the Blackstar{' '}
          <Link
            className={'underline'}
            target={'_blank'}
            href={'/blog/blackstar-beam-mini-is-available-now-powered-by-tone3000'}
          >
            Beam Mini
          </Link>{' '}
          and{' '}
          <Link className={'underline'} target={'_blank'} href={'/blog/blackstar-beam-solo-nam-a2'}>
            Beam Solo
          </Link>
          , the{' '}
          <Link className={'underline'} target={'_blank'} href={'/blog/headrush-nam-tone3000'}>
            HeadRush Prime, Core, and Flex Prime
          </Link>
          , the{' '}
          <Link
            className={'underline'}
            target={'_blank'}
            href={'/blog/darkglass-anagram-connects-directly-to-tone3000'}
          >
            Darkglass Anagram
          </Link>
          , the{' '}
          <Link
            className={'underline'}
            target={'_blank'}
            href={'/blog/tone3000-comes-to-lava-studio'}
          >
            LAVA STUDIO
          </Link>
          , the{' '}
          <Link
            className={'underline'}
            target={'_blank'}
            href={'/blog/chaos-audio-stratus-nimbus-nam-a2'}
          >
            Chaos Audio Stratus
          </Link>
          , the{' '}
          <Link
            className={'underline'}
            target={'_blank'}
            href={'/blog/sonulab-stompstation-now-supports-tone3000'}
          >
            Sonulab StompStation
          </Link>
          , the{' '}
          <Link
            className={'underline'}
            target={'_blank'}
            href={'/blog/vtr-effects-ignis-runs-nam-a2-natively'}
          >
            VTR Effects IGNIS
          </Link>
          , and many more have integrated the TONE3000 API.
        </p>
        <p>
          If you're interested in bringing TONE3000 into your products, check out the{' '}
          <Link className={'underline'} target={'_blank'} href={'/api'}>
            API Docs
          </Link>
          .
        </p>
      </div>
    ),
  },
];

export const supportSteps = [
  {
    title: 'How can I share ideas or request new features?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          Please spam us with your ideas and feature requests! Email{' '}
          <Link className={'underline'} href={'mailto:support@tone3000.com'}>
            support@tone3000.com
          </Link>{' '}
          or join the{' '}
          <Link className={'underline'} target={'_blank'} href={'https://discord.gg/wEV8uEWDnb'}>
            Discord
          </Link>
          .
        </p>
      </div>
    ),
  },
  {
    title: 'How can I contact support?',
    description: (
      <div className={'flex flex-col gap-4'}>
        <p>
          Email us at{' '}
          <Link className={'underline'} href={'mailto:support@tone3000.com'}>
            support@tone3000.com
          </Link>
          .
        </p>
      </div>
    ),
  },
];

export const fullFaqPlainText = [
  ...generalSteps.map((faq) => ({
    title: faq.title,
    description: getPlainTextFromStringOrElement(faq.description),
  })),
  ...gettingStartedSteps.map((faq) => ({
    title: faq.title,
    description: getPlainTextFromStringOrElement(faq.description),
  })),
  ...capturingGearSteps.map((faq) => ({
    title: faq.title,
    description: getPlainTextFromStringOrElement(faq.description),
  })),
  ...developersSteps.map((faq) => ({
    title: faq.title,
    description: getPlainTextFromStringOrElement(faq.description),
  })),
  ...supportSteps.map((faq) => ({
    title: faq.title,
    description: getPlainTextFromStringOrElement(faq.description),
  })),
];
